import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';

const JobFolderView = ({ jobId, quoteName, isVisible, onClose = () => {} }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [folderSummary, setFolderSummary] = useState(null);
  const { isAuthenticated, authFetch } = useAuth();


  const UPLOAD_API_URL = process.env.REACT_APP_UPLOAD_API_URL || '';

  const loadFolderContents = async () => {
    if (!jobId || !isAuthenticated) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await authFetch(`${UPLOAD_API_URL}/api/folder/files/${jobId}`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setFiles(data.files || []);
          setFolderSummary(data.summary || null);
        } else {
          setError('Failed to load folder contents');
        }
      } else if (response.status === 401) {
        // authFetch triggers logout/sessionExpired; keep UI message minimal.
        setError('Session expired. Please sign in again.');
      } else if (response.status === 404) {
        setError('Folder not found - may not have been created yet');
        setFiles([]);
        setFolderSummary(null);
      } else {
        setError(`Failed to load folder contents (${response.status})`);
      }
    } catch (err) {
      console.error('Error loading folder contents:', err);
      const errorMessage = err.message.includes('fetch') 
        ? 'Network error - please check your connection and try again'
        : 'Error loading folder contents - please try again';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isVisible && jobId) {
      loadFolderContents();
    }
  }, [isVisible, jobId, isAuthenticated]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFileIcon = (fileType, isJobsheet) => {
    if (isJobsheet) return '📄';
    if (fileType.includes('PDF')) return '📄';
    if (fileType.includes('Image')) return '🖼️';
    if (fileType.includes('Vector')) return '🎨';
    return '📁';
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Job Folder Contents</h2>
            <p className="text-sm text-gray-600">{quoteName || `Job ${jobId?.substring(0, 8)}...`}</p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={loadFolderContents}
              disabled={loading}
              className="text-sm text-blue-600 hover:text-blue-800 disabled:text-gray-400 px-3 py-1 rounded hover:bg-blue-50"
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-xl font-bold w-8 h-8 flex items-center justify-center rounded hover:bg-gray-100"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-2"></div>
              <p className="text-gray-600">Loading folder contents...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded p-4">
                <strong>Error:</strong> {error}
              </div>
              <button
                onClick={loadFolderContents}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Retry
              </button>
            </div>
          ) : (
            <>
              {/* Summary Section */}
              {folderSummary && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
                  <h3 className="font-medium text-gray-800 mb-3">Folder Summary</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Total Files:</span>
                      <span className="ml-2 font-medium">{folderSummary.total_files}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Size:</span>
                      <span className="ml-2 font-medium">{folderSummary.total_size_formatted}</span>
                    </div>
                    <div>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        folderSummary.has_jobsheet ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {folderSummary.jobsheet_files} Jobsheet{folderSummary.jobsheet_files !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        folderSummary.has_artwork ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {folderSummary.artwork_files} Artwork File{folderSummary.artwork_files !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                  
                  {folderSummary.ready_for_print && (
                    <div className="mt-3 text-center">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                        ✓ Ready for Print
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Files List */}
              <div>
                <h3 className="font-medium text-gray-800 mb-3">
                  Files ({files.length})
                </h3>
                
                {files.length > 0 ? (
                  <div className="space-y-2">
                    {files.map((file) => (
                      <div key={file.id} className="flex items-center justify-between p-3 bg-white rounded border hover:bg-gray-50">
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <span className="text-lg">{getFileIcon(file.file_type, file.is_jobsheet)}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {file.name}
                              {file.is_jobsheet && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                  Jobsheet
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-gray-500">
                              {file.size_formatted} • {file.file_type} • {formatDate(file.modified_date)}
                            </p>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <a
                            href={file.web_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium px-3 py-1 rounded hover:bg-blue-50"
                          >
                            View
                          </a>
                          <a
                            href={file.download_url}
                            download={file.name}
                            className="text-green-600 hover:text-green-800 text-sm font-medium px-3 py-1 rounded hover:bg-green-50"
                          >
                            Download
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <p>No files found in this job folder</p>
                    <p className="text-sm mt-1">Files will appear here once customers upload them</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default JobFolderView;